
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {TestRolloverDumpResponse} from './TestRolloverDumpResponse';
import {Date} from '../core/Date';
import {FieldModelDate} from '../core/Date';

/**
 * TestRolloverDumpRequest struct
 */
class TestRolloverDumpRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {UUID=} platformId
   * @param {string=} platformName
   * @param {!Date=} date
   * @constructor
   */
  constructor (parent = new Request(), platformId = null, platformName = null, date = new Date()) {
    super()
    super.copy(parent)
    this.PlatformId = platformId
    this.PlatformName = platformName
    this.Date = date
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TestRolloverDumpRequest}
   * @param {!TestRolloverDumpRequest} other Other struct
   * @returns {!TestRolloverDumpRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.PlatformId != null) {
      this.PlatformId = new UUID(other.PlatformId)
    } else {
      this.PlatformId = null
    }
    if (other.PlatformName != null) {
      this.PlatformName = other.PlatformName
    } else {
      this.PlatformName = null
    }
    if (other.Date != null) {
      this.Date = Date.fromObject(other.Date)
    } else {
      this.Date = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TestRolloverDumpRequest}
   * @returns {!TestRolloverDumpRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TestRolloverDumpRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TestRolloverDumpRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TestRolloverDumpRequest}
   * @param {!TestRolloverDumpRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TestRolloverDumpRequest)) {
      throw new TypeError('Instance of TestRolloverDumpRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TestRolloverDumpRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      PlatformId: ((this.PlatformId != null) ? this.PlatformId.toString() : null),
      PlatformName: ((this.PlatformName != null) ? this.PlatformName : null),
      Date: ((this.Date != null) ? this.Date : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TestRolloverDumpRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TestRolloverDumpRequest} other Object value
   * @returns {!TestRolloverDumpRequest} Created struct
   */
  static fromObject (other) {
    return new TestRolloverDumpRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TestRolloverDumpRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TestRolloverDumpRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TestRolloverDumpRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6263
  }
}

export { TestRolloverDumpRequest };

/**
 * Fast Binary Encoding TestRolloverDumpRequest field model
 */
class FieldModelTestRolloverDumpRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.PlatformId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.PlatformName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.PlatformId.fbeOffset + this.PlatformId.fbeSize), buffer, this.PlatformId.fbeOffset + this.PlatformId.fbeSize)
    this.Date = new FieldModelDate(buffer, this.PlatformName.fbeOffset + this.PlatformName.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.PlatformId.fbeSize + this.PlatformName.fbeSize + this.Date.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.PlatformId.fbeExtra + this.PlatformName.fbeExtra + this.Date.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTestRolloverDumpRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6263
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.PlatformId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PlatformId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PlatformId.fbeSize

    if ((fbeCurrentSize + this.PlatformName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PlatformName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PlatformName.fbeSize

    if ((fbeCurrentSize + this.Date.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Date.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Date.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {!TestRolloverDumpRequest} fbeValue Default value, defaults is new TestRolloverDumpRequest()
   * @returns {!TestRolloverDumpRequest} TestRolloverDumpRequest value
   */
  get (fbeValue = new TestRolloverDumpRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {!TestRolloverDumpRequest} fbeValue TestRolloverDumpRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.PlatformId.fbeSize) <= fbeStructSize) {
      fbeValue.PlatformId = this.PlatformId.get()
    } else {
      fbeValue.PlatformId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PlatformId.fbeSize

    if ((fbeCurrentSize + this.PlatformName.fbeSize) <= fbeStructSize) {
      fbeValue.PlatformName = this.PlatformName.get()
    } else {
      fbeValue.PlatformName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PlatformName.fbeSize

    if ((fbeCurrentSize + this.Date.fbeSize) <= fbeStructSize) {
      fbeValue.Date = this.Date.get()
    } else {
      fbeValue.Date = new Date()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Date.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTestRolloverDumpRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {!TestRolloverDumpRequest} fbeValue TestRolloverDumpRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTestRolloverDumpRequest}
   * @param {TestRolloverDumpRequest} fbeValue TestRolloverDumpRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.PlatformId.set(fbeValue.PlatformId)
    this.PlatformName.set(fbeValue.PlatformName)
    this.Date.set(fbeValue.Date)
  }
}

export { FieldModelTestRolloverDumpRequest };

/**
 * Fast Binary Encoding TestRolloverDumpRequest model
 */
class TestRolloverDumpRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTestRolloverDumpRequest(this.buffer, 4)
  }

  /**
   * Get the TestRolloverDumpRequest model
   * @this {!TestRolloverDumpRequestModel}
   * @returns {!FieldModelTestRolloverDumpRequest} model TestRolloverDumpRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TestRolloverDumpRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TestRolloverDumpRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TestRolloverDumpRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TestRolloverDumpRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTestRolloverDumpRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TestRolloverDumpRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TestRolloverDumpRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TestRolloverDumpRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TestRolloverDumpRequestModel}
   * @param {!TestRolloverDumpRequest} value TestRolloverDumpRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TestRolloverDumpRequestModel}
   * @param {!TestRolloverDumpRequest} value TestRolloverDumpRequest value, defaults is new TestRolloverDumpRequest()
   * @return {!object} Deserialized TestRolloverDumpRequest value and its size
   */
  deserialize (value = new TestRolloverDumpRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TestRolloverDumpRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TestRolloverDumpRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TestRolloverDumpRequestModel}
   * @param {!number} prev Previous TestRolloverDumpRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TestRolloverDumpRequestModel };
TestRolloverDumpRequest.__has_response = true;
TestRolloverDumpRequest.__response_class = TestRolloverDumpResponse;
